import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { GetLetterHeadAction } from "../../../../../store/Ancillary/LetterHead/Action";
import {
  GetCompanyLogoHelper,
  GetCompanySignedLogoHelper,
} from "../../../Ancillary/CompanyDetails/helper/CompanyDetailsHelper";
import { GetOfferLetterTemplateResponseHelper } from "../../../ATSSettings/helper/AtsFormTemplateResponseHelper";
import {
  GetOfferLetterTemplateHelper,
  OfferLetterFieldsHelper,
} from "../../../ATSSettings/helper/AtsFormTemplateHelper";
import { getUserData } from "../../../Base/helper/baseFunctions";
import OfferLetterDocumentEditor, {
  generateOfferLetterPdf,
  parseOfferLetterContent,
  base64ToUint8Array,
} from "../../../ATSSettings/views/OfferLetter/editor/OfferLetterDocumentEditor";
import JSZip from "jszip";
import {
  buildCandidateSalaryFieldValues,
  syncHeaderAndFooterInDocxZip,
  substituteVariablesInDocxXml,
  stripAnnexureFromDocXml,
} from "../../../ATSSettings/views/OfferLetter/editor/editorDocxUtils";

const ViewOffer = (props) => {
  const data = props.data || {};
  const dispatch = useDispatch();
  const [imageUrl, setImageUrl] = useState("");
  const { letterHeadData } = useSelector(
    (state) => state?.AncillaryReducer?.LetterHeadReducer
  );
  const [offer, setOfferData] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [templateFields, setTemplateFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDocxLoading, setIsDocxLoading] = useState(false);
  const [isFetchingOffer, setIsFetchingOffer] = useState(false);
  const previewEditorRef = useRef(null);
  const userData = getUserData();

  useEffect(() => {
    const responseId =
      data?.offer_letter_response_id ||
      data?.candidate_offer_letter_response_id ||
      data?.id ||
      props.match?.params?.guid;

    if (responseId) {
      setIsFetchingOffer(true);
      GetOfferLetterTemplateResponseHelper({
        workspace: userData?.domain,
        offer_letter_response_id: responseId,
      })
        .then((res) => {
          const item = res?.data?.data?.[0];
          if (item) {
            setOfferData(item);
            if (item?.offer_letter_template_fields) {
              setTemplateFields(item.offer_letter_template_fields);
            }
            if (item?.offer_letter_template_id) {
              OfferLetterFieldsHelper({ offer_letter_template_id: item.offer_letter_template_id }).then((fRes) => {
                if (fRes?.data?.statuscode === 200 && Array.isArray(fRes?.data?.data)) {
                  setTemplateFields(fRes.data.data);
                }
              });
              GetOfferLetterTemplateHelper({
                workspace: userData?.domain,
                offer_letter_template_id: item?.offer_letter_template_id,
              }).then((tRes) => {
                setTemplateData(tRes?.data?.data?.[0] || null);
              });
            }
          }
        })
        .finally(() => {
          setIsFetchingOffer(false);
        });
    }
    dispatch(GetLetterHeadAction());
    GetCompanySignedLogoHelper()
      .then((res) => {
        if (res?.signedUrl) {
          setImageUrl(res.signedUrl);
        } else if (res?.data?.data?.[0]?.companylogo) {
          setImageUrl(res.data.data[0].companylogo);
        }
      })
      .catch(() => {
        GetCompanyLogoHelper().then((res) => {
          setImageUrl(res?.data?.data?.[0]?.companylogo || "");
        });
      });
    //eslint-disable-next-line
  }, [data, props.match?.params?.guid]);

  const rawOfferContent =
    offer?.offer_letter_edited ||
    offer?.offer_letter_response ||
    templateData?.offer_letter_content ||
    "";

  let payConfigInput = null;
  try {
    const rawPayJson = offer?.payconfig_input_json || data?.payconfig_input_json;
    payConfigInput = typeof rawPayJson === "string" ? JSON.parse(rawPayJson) : (rawPayJson || null);
  } catch (e) {}

  const isCtcZero = offer?.is_configure_ctc === false || Boolean(
    payConfigInput && (
      (Number(payConfigInput.ctc) === 0 || payConfigInput.ctc === "0") ||
      (Number(payConfigInput.grossEarningsTempTotalValue || 0) === 0 && Number(payConfigInput.basic || 0) === 0)
    )
  );

  const payConfigHtml = isCtcZero ? "" : (offer?.payconfig_content || "");

  let fieldValues = {};
  try {
    const rawFields = offer?.offer_letter_fields_response || data?.offer_letter_fields_response;
    fieldValues = typeof rawFields === "string" ? JSON.parse(rawFields) : (rawFields || {});
  } catch (e) {}

  const candidateSalaryFieldValues = buildCandidateSalaryFieldValues(
    payConfigInput || offer?.payconfig_input_json || data?.payconfig_input_json
  );
  const effectiveFieldValues = { ...candidateSalaryFieldValues, ...fieldValues };

  const offerLetterNo = offer?.offer_letter_no || data?.offer_letter_no;
  if (offerLetterNo) {
    effectiveFieldValues.offer_letter_no = offerLetterNo;
    effectiveFieldValues["@{offer_letter_no}"] = offerLetterNo;
  }

  const candidateName = data?.full_name;
  if (candidateName && !effectiveFieldValues.candidate_name) {
    effectiveFieldValues.candidate_name = candidateName;
    effectiveFieldValues["@{candidate_name}"] = candidateName;
  }

  const rawFieldsList =
    templateFields.length > 0
      ? templateFields
      : (offer?.offer_letter_template_fields || templateData?.offer_letter_template_fields || []);
  const mentionUsers = rawFieldsList.map((temp) => ({
    ...temp,
    id: temp?.name || temp?.label,
    name: temp?.name || temp?.label,
    label: temp?.label || temp?.name,
    display: temp?.label || temp?.name,
  }));

  const parsed = parseOfferLetterContent(rawOfferContent);
  const isLetterheadMode = Boolean(
    offer?.is_letterhead ||
    templateData?.is_letterhead ||
    parsed?.header_footer_mode === "LETTERHEAD"
  );

  const effectiveLetterHead = Array.isArray(letterHeadData) ? letterHeadData[0] : letterHeadData;
  const effectiveHeader = offer?.header || templateData?.header || effectiveLetterHead?.letterhead_header || "";
  const effectiveFooter = offer?.footer || templateData?.footer || effectiveLetterHead?.letterhead_footer || "";

  const handleDownloadPdf = async () => {
    try {
      setIsLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 40));

      const activeDocBytes = previewEditorRef.current?.getDocBytes?.() || null;

      const pdf = await generateOfferLetterPdf({
        content: rawOfferContent,
        docBytes: activeDocBytes,
        payConfigContent: isCtcZero ? "" : payConfigHtml,
        isCtcZero: isCtcZero,
        fieldValues: effectiveFieldValues,
        isLetterhead: isLetterheadMode,
        header_footer_mode: isLetterheadMode ? "LETTERHEAD" : "OUR_HEADER_FOOTER",
        letterHeadData: isLetterheadMode ? effectiveLetterHead : null,
        companylogo: isLetterheadMode ? null : imageUrl,
        headerTitle: isLetterheadMode ? "" : effectiveHeader,
        footerText: isLetterheadMode ? "" : effectiveFooter,
      });

      if (pdf && typeof pdf.save === "function") {
        pdf.save(`${data?.full_name || "offer_letter"}.pdf`);
      }
    } catch (err) {
      console.error("[ViewOffer] Error downloading PDF:", err);
      toast.error("Failed to generate PDF");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadDocx = async () => {
    try {
      setIsDocxLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 40));

      let activeDocBytes = previewEditorRef.current?.getDocBytes?.() || null;
      if (!activeDocBytes && previewEditorRef.current?.saveDocx) {
        const saved = await previewEditorRef.current.saveDocx();
        activeDocBytes = saved?.bytes || null;
      }

      if (!activeDocBytes) {
        const rawDocContent = offer?.offer_letter_edited || rawOfferContent;
        if (rawDocContent) {
          const parsedDoc = parseOfferLetterContent(rawDocContent);
          if (parsedDoc?.docxBase64) {
            activeDocBytes = base64ToUint8Array(parsedDoc.docxBase64);
          }
        }
      }

      if (!activeDocBytes) {
        toast.error("Document is still loading or unavailable for download");
        return;
      }

      const zip = await JSZip.loadAsync(activeDocBytes);
      await syncHeaderAndFooterInDocxZip(zip, {
        headerTitle: isLetterheadMode ? "" : effectiveHeader,
        footerText: isLetterheadMode ? "" : effectiveFooter,
        companylogo: isLetterheadMode ? null : imageUrl,
        header_footer_mode: isLetterheadMode ? "LETTERHEAD" : "OUR_HEADER_FOOTER",
        letterHeadData: isLetterheadMode ? effectiveLetterHead : null,
        top_margin: parsed?.top_margin || 45,
        bottom_margin: parsed?.bottom_margin || 25,
      });

      const docXmlFile = zip.file("word/document.xml");
      if (docXmlFile) {
        let docXml = await docXmlFile.async("text");
        if (isCtcZero) {
          docXml = stripAnnexureFromDocXml(docXml);
        }
        if (effectiveFieldValues && Object.keys(effectiveFieldValues).length > 0) {
          docXml = substituteVariablesInDocxXml(docXml, effectiveFieldValues);
          zip.file("word/document.xml", docXml);
        } else if (isCtcZero) {
          zip.file("word/document.xml", docXml);
        }
      }

      const finalBytes = await zip.generateAsync({ type: "uint8array" });

      const blob = new Blob([finalBytes], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${data?.full_name || "offer_letter"}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[ViewOffer] Error downloading DOCX:", err);
      toast.error("Failed to download DOCX");
    } finally {
      setIsDocxLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box sx={{ width: "100%", maxWidth: "1000px", mb: 2 }}>
        <Typography sx={{ mb: 1, mt: 1, textAlign: "center", fontWeight: 600 }} variant="h6">
          {data?.title || "Offer Letter"}
        </Typography>
      </Box>

      <Box sx={{ width: "100%", maxWidth: "1000px" }}>
        {isFetchingOffer && !offer && !rawOfferContent ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
              gap: 2,
            }}
          >
            <CircularProgress size={36} sx={{ color: "#8b44ff" }} />
            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
              Loading offer letter preview...
            </Typography>
          </Box>
        ) : (
          <OfferLetterDocumentEditor
            ref={previewEditorRef}
            value={rawOfferContent}
            payConfigContent={isCtcZero ? "" : payConfigHtml}
            isCtcZero={isCtcZero}
            fieldValues={effectiveFieldValues}
            mentionUsers={mentionUsers}
            header_footer_mode={isLetterheadMode ? "LETTERHEAD" : "OUR_HEADER_FOOTER"}
            letterHeadData={effectiveLetterHead}
            companylogo={imageUrl}
            headerTitle={effectiveHeader}
            footerText={effectiveFooter}
            mode="view"
            editorTitle="Offer Letter Preview"
          />
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3, pb: 2 }}>
        <Button
          variant="contained"
          className="mpl-primary-btn"
          onClick={handleDownloadPdf}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          disabled={isLoading || isDocxLoading}
        >
          {isLoading ? "Generating PDF..." : "Download PDF"}
        </Button>
        <Button
          variant="outlined"
          sx={{
            borderColor: "#8b44ff",
            color: "#8b44ff",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": { borderColor: "#7c3aed", backgroundColor: "#f5f3ff" },
          }}
          onClick={handleDownloadDocx}
          startIcon={isDocxLoading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          disabled={isLoading || isDocxLoading}
        >
          {isDocxLoading ? "Downloading DOCX..." : "Download DOCX"}
        </Button>
      </Box>
    </Box>
  );
};

export default ViewOffer;
